import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Sparkles, Inbox, CheckCircle2, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { monthLabelIST } from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";
import { plannedDoctorsLabel } from "./plannedDoctors";
import StickyHeading from "../../components/common/StickyHeading";

const ALL = "__all__";

// One row per action kind, so the dialog's title, its button and the toast
// cannot describe three different things. The kind IS the endpoint segment.
const ACTION_TITLE = { approve: "Approve plan", "send-back": "Send back plan", reopen: "Reopen plan" };
const ACTION_CTA = { approve: "Approve", "send-back": "Send back", reopen: "Reopen" };
const ACTION_DONE = {
  approve: "Plan approved",
  "send-back": "Plan sent back",
  reopen: "Plan reopened for editing",
};
const ACTION_PLACEHOLDER = {
  approve: "Optional remark",
  "send-back": "Reason / changes needed (required)",
  reopen: "Why the plan is being reopened (required)",
};

export default function AllTourPlansPage() {
  const [plans, setPlans] = useState([]);
  const [sos, setSos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [soId, setSoId] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [actionMode, setActionMode] = useState(null);
  const [remark, setRemark] = useState("");
  const [acting, setActing] = useState(false);

  // NO ROLE CLAUSE — see `AllExpensesPage.jsx`. `plan.can_approve` is the
  // backend's approval rule and is the only thing that may gate the buttons.

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        // Sales Managers own doctors and plan their own routes, so they are
        // selectable here alongside Sales Officers.
        setSos(
          data.filter(
            (u) => ["sales_officer", "assistant_manager"].includes(u.role) && u.is_active
          )
        );
      } catch (_) {}
    })();
  }, []);

  // Named, because a decision now has to re-read the list rather than patch it
  // in place: reopen changes the status AND clears the approval fields the card
  // renders, and a local guess about those is a second copy of the endpoint's
  // rule. The sequence number keeps the old race guard — a response from a
  // superseded filter must not overwrite a newer one.
  const requestSeq = useRef(0);
  const load = useCallback(async () => {
    const params = {};
    if (soId !== ALL) params.so_id = soId;
    if (status !== ALL) params.status = status;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const { data } = await api.get("/tour-plans", { params });
      if (seq === requestSeq.current) setPlans(data);
    } catch (e) {
      if (seq === requestSeq.current) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [soId, status]);

  useEffect(() => { load(); }, [load]);

  async function submitAction() {
    if (!actionMode) return;
    // Reopen takes the send-back rule: an approved month is being taken back
    // off the officer's record, and the reason is the only thing that tells
    // them why it moved.
    if (actionMode.kind !== "approve" && !remark.trim()) {
      toast.error("Add a remark");
      return;
    }
    setActing(true);
    try {
      await api.put(`/tour-plans/${actionMode.plan.id}/${actionMode.kind}`, { remark: remark.trim() || null });
      toast.success(ACTION_DONE[actionMode.kind]);
      setActionMode(null);
      setRemark("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="all-tour-plans-page">
      <StickyHeading
        icon={Sparkles}
        label="Company Tour Plans"
        title="All tour plans"
        testId="all-tour-plans-heading"
      />

      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4 grid grid-cols-2 gap-2">
        <Select value={soId} onValueChange={setSoId}>
          <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="all-tour-plan-filter-so">
            <SelectValue placeholder="Officer / manager" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All officers &amp; managers</SelectItem>
            {sos.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="all-tour-plan-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Submitted">Submitted</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0,1,2,3,4,5].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <Inbox className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">No tour plans match</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="all-tour-plans-grid">
          {plans.map((p) => <PlanCard key={p.id} plan={p} setActionMode={setActionMode} />)}
        </div>
      )}

      <Dialog
        open={!!actionMode}
        onOpenChange={(o) => {
          if (!o) {
            setActionMode(null);
            setRemark("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ACTION_TITLE[actionMode?.kind]}</DialogTitle>
            <DialogDescription>
              {actionMode?.plan?.sales_officer_name} · {actionMode && monthLabelIST(actionMode.plan.year, actionMode.plan.month)}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder={ACTION_PLACEHOLDER[actionMode?.kind]}
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-tour-plan-remark"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionMode(null);
                setRemark("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              disabled={acting}
              className={
                actionMode?.kind === "approve" ? "bg-emerald-600 hover:bg-emerald-700"
                : actionMode?.kind === "reopen" ? "bg-amber-600 hover:bg-amber-700"
                : "bg-red-600 hover:bg-red-700"
              }
              data-testid="all-tour-plan-confirm"
            >
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : ACTION_CTA[actionMode?.kind]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlanCard({ plan, setActionMode }) {
  return (
    <div className="bg-white border border-callisto rounded-xl p-4" data-testid={`tour-plan-card-${plan.id}`}>
      <Link to={`/route-planner/${plan.id}`} className="block group">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-callisto-navy group-hover:underline truncate">
            {plan.sales_officer_name}
          </h3>
          <span
            className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
              plan.status === "Approved"
                ? "bg-emerald-50 text-emerald-700"
                : plan.status === "Submitted"
                ? "bg-amber-50 text-amber-700"
                : "bg-slate-100 text-slate-700"
            }`}
          >
            {plan.status}
          </span>
        </div>
        <p className="mt-1 text-xs text-callisto-muted">
          {joinParts([
            monthLabelIST(plan.year, plan.month),
            `${plural(plan.daily_plan?.length || 0, "day")} planned`,
            // Not `0 doctors` when the plan never named any — see ./plannedDoctors.
            plannedDoctorsLabel(plan),
          ])}
        </p>
        {plan.approver_remark && (
          <p className="mt-1.5 text-[11px] italic text-callisto-muted">"{plan.approver_remark}"</p>
        )}
      </Link>
      {/* The whole row used to hang off `status === "Submitted"`, which is
          where Reopen would never have appeared. The gate is per ACTION now:
          `can_approve` still decides whether any of them render. */}
      {plan.can_approve && plan.status === "Submitted" && (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            onClick={() => setActionMode({ plan, kind: "approve" })}
            className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
            data-testid={`all-tour-plan-approve-${plan.id}`}
          >
            <CheckCircle2 size={12} className="mr-1" />
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setActionMode({ plan, kind: "send-back" })}
            className="h-8 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
            data-testid={`all-tour-plan-sendback-${plan.id}`}
          >
            <Undo2 size={12} className="mr-1" />
            Send back
          </Button>
        </div>
      )}
      {/* `can_reopen`, NOT `can_approve`: the latter is the STAGE gate, so on
          a decided plan it names only whoever signed last and hides Reopen
          from the officer's own Sales Manager. The server derives this one
          from chain membership — see `team_scope.can_reopen_submission`. */}
      {plan.can_reopen && (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setActionMode({ plan, kind: "reopen" })}
            className="h-8 text-[11px] border-amber-200 text-amber-700 hover:bg-amber-50"
            data-testid={`all-tour-plan-reopen-${plan.id}`}
          >
            <Undo2 size={12} className="mr-1" />
            Reopen
          </Button>
        </div>
      )}
    </div>
  );
}
